import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Skeleton,
  Typography,
  styled,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { format, parseISO, isValid } from "date-fns";
import { useApproval } from "@/hooks/use-approval";
import { formatApprovalLabel, getApprovalStatusMeta } from "../../data";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    maxWidth: 520,
    width: "100%",
  },
}));

const Field = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500}>
      {value ?? "N/A"}
    </Typography>
  </Box>
);

const formatDateTime = (date, pattern = "MMM d, yyyy, h:mm a") => {
  if (!date) return "N/A";
  const parsed = parseISO(date);
  return isValid(parsed) ? format(parsed, pattern) : "N/A";
};

// GET /approvals/{module}/{id} returns the *underlying resource* for that
// module (a report, a dispute, an account, ...), not a generic approval
// shape - its fields differ per module. The queue row (`item`, from
// GET /approvals) is what has the normalized module/action/target/status
// fields, so that's the source for the header. Anything else the detail
// call returns beyond `activity` gets rendered generically below rather
// than hardcoded per module.
const KNOWN_DETAIL_KEYS = new Set(["id", "status", "activity", "createdById", "approvedById"]);

const formatDetailValue = (value) => {
  if (value === null || value === undefined || value === "") return "N/A";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "N/A";
  return String(value);
};

export function ApprovalDetailDrawer({ open, onClose, item }) {
  const { data, isLoading } = useApproval(item?.module, item?.id, {
    enabled: open && Boolean(item?.module) && Boolean(item?.id),
  });
  const detail = data?.data;
  const statusMeta = getApprovalStatusMeta(item?.status);

  const extraEntries = detail
    ? Object.entries(detail).filter(([key]) => !KNOWN_DETAIL_KEYS.has(key))
    : [];

  return (
    <StyledDialog open={open} onClose={onClose}>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        Approval Request
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600} sx={{ fontSize: "1rem" }}>
            {formatApprovalLabel(item?.action)}
          </Typography>
          {item?.actionDetail && (
            <Typography variant="body2" color="text.secondary">
              {item.actionDetail}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: "inline-flex",
            alignSelf: "flex-start",
            px: 1.5,
            py: 0.3,
            borderRadius: "999px",
            fontSize: "0.8rem",
            fontWeight: 500,
            backgroundColor: statusMeta.bg,
            color: statusMeta.color,
          }}
        >
          {statusMeta.label}
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <Field label="Module" value={formatApprovalLabel(item?.module)} />
          <Field
            label="Target"
            value={`${item?.target ?? "N/A"}${
              item?.targetType ? ` (${formatApprovalLabel(item.targetType)})` : ""
            }`}
          />
          <Field label="Initiated By" value={item?.initiatedByName} />
          <Field label="Submitted" value={formatDateTime(item?.submittedAt)} />
        </Box>

        {isLoading ? (
          <Skeleton variant="rounded" height={120} />
        ) : (
          <>
            {extraEntries.length > 0 && (
              <>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    Details
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {extraEntries.map(([key, value]) => (
                      <Field
                        key={key}
                        label={formatApprovalLabel(key)}
                        value={formatDetailValue(value)}
                      />
                    ))}
                  </Box>
                </Box>
              </>
            )}

            <Divider />

            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Activity
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {detail?.activity?.length ? (
                  detail.activity.map((entry) => (
                    <Box
                      key={entry.id}
                      sx={{
                        borderLeft: "2px solid",
                        borderColor: "divider",
                        pl: 1.5,
                      }}
                    >
                      <Typography variant="body2" fontWeight={500}>
                        {formatApprovalLabel(entry.action)}{" "}
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                        >
                          by {entry.actorName ?? "N/A"}
                        </Typography>
                      </Typography>
                      {entry.note && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          {entry.note}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(entry.occurredAt)}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No activity recorded yet.
                  </Typography>
                )}
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
    </StyledDialog>
  );
}

export default ApprovalDetailDrawer;
